/**
 * Events Landing Page
 *
 * SEO-optimized landing page targeting event organizers.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSEO, useHreflang, useStructuredData, SITE_CONFIG } from '../../lib/seo';
import PublicLayout from '../../components/layout/PublicLayout';

export default function EventsPage() {
  const { i18n } = useTranslation();

  useSEO({
    title: 'Event Registration & Live Polling | Conferences, Meetups & Webinars | xtFORM',
    description: 'Create event registration forms, live audience polls, and interactive Q&A sessions. Perfect for conferences, workshops, webinars, and team events. Free to use.',
    keywords: 'event registration forms, live polling, audience engagement, conference tools, webinar polls, Q&A forms, event feedback, workshop registration, meetup forms',
    url: `${SITE_CONFIG.domain}/events`,
    locale: i18n.language,
  });

  useHreflang('/events');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Event Registration & Live Polling',
    'description': 'Interactive forms and polls for event organizers.',
    'url': `${SITE_CONFIG.domain}/events`,
    'mainEntity': {
      '@type': 'SoftwareApplication',
      'name': 'xtFORM for Events',
      'applicationCategory': 'EventApplication',
      'operatingSystem': 'Web',
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD',
      },
    },
  };

  useStructuredData(structuredData, 'events-page');

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-pink-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-6">
              <span className="text-lg">🎪</span>
              For Event Organizers
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Engage Your Audience with
              <span className="text-purple-600"> Interactive Events</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              From registration to live polling, create memorable event experiences.
              Perfect for conferences, workshops, webinars, and team gatherings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-purple-600 text-white rounded-xl font-semibold text-lg hover:bg-purple-700 transition-colors shadow-lg shadow-purple-500/30"
              >
                Create Free Account
              </Link>
              <Link
                to="/demo/poll"
                className="px-8 py-4 border-2 border-purple-600 text-purple-600 rounded-xl font-semibold text-lg hover:bg-purple-50 transition-colors"
              >
                Try Live Polling
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Event Lifecycle */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Tools for Every Stage of Your Event
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From planning to follow-up, we help you create engaging experiences.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Before */}
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg z-10">
                1
              </div>
              <div className="pt-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 h-full">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">Before the Event</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Registration Forms</strong>
                      <p className="text-gray-600 text-sm">Collect attendee info, dietary preferences, session choices</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Speaker Submissions</strong>
                      <p className="text-gray-600 text-sm">Accept talk proposals with file uploads</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Pre-Event Surveys</strong>
                      <p className="text-gray-600 text-sm">Understand attendee expectations</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* During */}
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg z-10">
                2
              </div>
              <div className="pt-16 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-8 text-white h-full">
                <h3 className="text-xl font-semibold mb-4 text-center">During the Event</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>Live Polls</strong>
                      <p className="text-purple-200 text-sm">Real-time audience voting with instant results</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>Interactive Q&A</strong>
                      <p className="text-purple-200 text-sm">Collect and prioritize audience questions</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>Live Quiz Games</strong>
                      <p className="text-purple-200 text-sm">Icebreakers and trivia with leaderboards</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-200 mt-1">✓</span>
                    <div>
                      <strong>Word Clouds</strong>
                      <p className="text-purple-200 text-sm">Visual brainstorming with audience input</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* After */}
            <div className="relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold text-lg z-10">
                3
              </div>
              <div className="pt-16 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 h-full">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">After the Event</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Feedback Surveys</strong>
                      <p className="text-gray-600 text-sm">NPS scores, session ratings, speaker feedback</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Export & Analyze</strong>
                      <p className="text-gray-600 text-sm">Download data in CSV, JSON, or Excel</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-500 mt-1">✓</span>
                    <div>
                      <strong className="text-gray-900">Follow-up Forms</strong>
                      <p className="text-gray-600 text-sm">Interest forms for future events</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Perfect for Any Event Type
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🎤</div>
              <h3 className="font-semibold text-gray-900 mb-2">Conferences</h3>
              <p className="text-gray-600 text-sm">Multi-day events with sessions, speakers, and networking</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">💻</div>
              <h3 className="font-semibold text-gray-900 mb-2">Webinars</h3>
              <p className="text-gray-600 text-sm">Online presentations with live audience interaction</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🛠️</div>
              <h3 className="font-semibold text-gray-900 mb-2">Workshops</h3>
              <p className="text-gray-600 text-sm">Hands-on sessions with exercises and assessments</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🏢</div>
              <h3 className="font-semibold text-gray-900 mb-2">Team Events</h3>
              <p className="text-gray-600 text-sm">All-hands meetings, offsites, and team building</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🎓</div>
              <h3 className="font-semibold text-gray-900 mb-2">Training</h3>
              <p className="text-gray-600 text-sm">Corporate training with knowledge checks</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="font-semibold text-gray-900 mb-2">Meetups</h3>
              <p className="text-gray-600 text-sm">Community events with RSVPs and polls</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">🎉</div>
              <h3 className="font-semibold text-gray-900 mb-2">Social Events</h3>
              <p className="text-gray-600 text-sm">Parties, celebrations with games and RSVPs</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">📚</div>
              <h3 className="font-semibold text-gray-900 mb-2">Academic</h3>
              <p className="text-gray-600 text-sm">Symposiums, lectures, and research presentations</p>
            </div>
          </div>
        </div>
      </section>

      {/* How Live Polling Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Live Polling in 3 Simple Steps
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-600 font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Create Your Poll</h3>
                    <p className="text-gray-600">Use our drag-and-drop builder to create engaging polls in minutes.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-600 font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Share the Code</h3>
                    <p className="text-gray-600">Display a QR code or short link. Attendees join instantly from any device.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 text-purple-600 font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Watch Results Live</h3>
                    <p className="text-gray-600">See responses appear in real-time on your presenter screen.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-8">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="text-sm text-gray-500 mb-2">Join at</div>
                  <div className="text-2xl font-bold text-purple-600">xtform.com/play</div>
                </div>
                <div className="border-t border-gray-100 pt-6">
                  <div className="text-center mb-4">
                    <div className="text-sm text-gray-500 mb-1">Game PIN</div>
                    <div className="text-4xl font-mono font-bold text-gray-900 tracking-widest">283 194</div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-4xl">📱</span>
                    </div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-4">
                    Scan QR code to join
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Make Your Next Event Unforgettable
          </h2>
          <p className="text-purple-100 text-lg mb-8">
            Join thousands of event organizers using xtFORM to engage their audiences.
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-4 bg-white text-purple-600 rounded-xl font-semibold text-lg hover:bg-purple-50 transition-colors"
          >
            Get Started for Free
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
